import React, { useCallback, useMemo, useRef, useState } from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { FocusRestorationContext } from '../../controllers/focus-restoration-context.tsx';

// The idea of this component is to provide a focus restoration context for the project menu items.
// The context will be read deep down the tree where the menuItem which supports drag-and-drop is rendered.

function ContentWithDragAndDropEnabled({ children }: { children: React.ReactNode }) {
	const shouldRestoreFocusRef = useRef<boolean>(false);
	const [shouldRestoreFocus, setShouldRestoreFocus] = useState<boolean>(false);

	const enableRestoreFocus = useCallback(() => {
		if (fg('jfp-magma-fix-project-nav-move-focus-restoration')) {
			setShouldRestoreFocus(true);
		} else {
			shouldRestoreFocusRef.current = true;
		}
	}, []);

	const clearRestoreFocus = useCallback(() => {
		if (fg('jfp-magma-fix-project-nav-move-focus-restoration')) {
			setShouldRestoreFocus(false);
		} else {
			shouldRestoreFocusRef.current = false;
		}
	}, []);

	const contextValue = useMemo(
		() => ({
			shouldRestoreFocusRef, // remove on cleanup of jfp-magma-fix-project-nav-move-focus-restoration
			shouldRestoreFocus,
			enableRestoreFocus,
			clearRestoreFocus,
		}),
		[clearRestoreFocus, enableRestoreFocus, shouldRestoreFocus],
	);

	return (
		<FocusRestorationContext.Provider value={contextValue}>
			{children}
		</FocusRestorationContext.Provider>
	);
}

export const ContentWithDragAndDrop = componentWithCondition(
	() => expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false),
	ContentWithDragAndDropEnabled,
	({ children }: { children: React.ReactNode }) => children,
);
